import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { fetchAuthSession } from "aws-amplify/auth";

// === Config (env-first, falls back to your provided values) ===
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_test_51RNAp3PFQWdJoEw84QEWGAngGJEcCNunykbYfGbUHQrEr6WmxXvu7vYyt2m4MZxzyqEZ0Ta37y2o2hbd4CP1bqyY009IA4KeFr";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://bp7u0bhrc8.execute-api.eu-west-2.amazonaws.com/dev";

const PRICE_ID =
  process.env.REACT_APP_STRIPE_PRICE_ID ||
  "price_1RSkJpPFQWdJoEw8b2f9dU1v";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Small fetch helper (JSON in/out + error surfacing)
async function http(url, init = {}) {
  const res = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function SubscriptionTestPage() {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [customerId, setCustomerId] = useState(
    localStorage.getItem("stripeCustomerId") || ""
  );
  const [statusOut, setStatusOut] = useState(null);
  const [meOut, setMeOut] = useState(null);

  // --- new: cached idToken + precreated checkout url
  const [cachedIdToken, setCachedIdToken] = useState(null);
  const [precreatedCheckoutUrl, setPrecreatedCheckoutUrl] = useState(null);

  // show who we are (decoded claims) for sanity when testing
  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) return;
        const [, payload] = idToken.split(".");
        const claims = JSON.parse(atob(payload));
        setMeOut({
          sub: claims?.sub,
          email: claims?.email,
          "cognito:username": claims?.["cognito:username"],
        });
      } catch {
        // ignore
      }
    })();
  }, []);

  // --- new: attempt to precreate a checkout session once we have an auth token
  useEffect(() => {
    let mounted = true;
    const precreate = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) return;
        setCachedIdToken(idToken);

        const body = {
          priceId: PRICE_ID,
          successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
        };

        const { url } = await http(`${API_BASE}/create-checkout-session`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(body),
        });

        if (mounted && url) {
          setPrecreatedCheckoutUrl(url);
          console.debug("Precreated checkout URL");
        }
      } catch (err) {
        console.debug("Precreate checkout failed:", err.message);
      }
    };

    precreate();
    return () => {
      mounted = false;
    };
  }, []);

  // Start subscription: prefer precreated URL, fallback to on-demand create
  const startSubscription = async () => {
    try {
      setBusy(true);
      setNote("Creating checkout session…");

      // if precreated URL exists, redirect immediately
      if (precreatedCheckoutUrl) {
        window.location.assign(precreatedCheckoutUrl);
        return;
      }

      // fallback: use cached token if present to reduce extra auth calls
      const idToken =
        cachedIdToken || (await fetchAuthSession()).tokens?.idToken?.toString();
      if (!idToken) throw new Error("Not signed in");

      const { url, portalUrl, customerId: cidFromCreate } = await http(
        `${API_BASE}/create-checkout-session`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            priceId: PRICE_ID,
            successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/billing`,
          }),
        }
      );

      if (cidFromCreate && /^cus_/.test(cidFromCreate)) {
        localStorage.setItem("stripeCustomerId", cidFromCreate);
        setCustomerId(cidFromCreate);
      }

      const redirectUrl = url || portalUrl;
      if (!redirectUrl) throw new Error("No redirect URL from backend");
      window.location.assign(redirectUrl);
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  // Authenticated call to GET /me/subscription (uses Cognito userId via GSI)
  const checkMySubscription = async () => {
    try {
      setBusy(true);
      setNote("Fetching my subscription status…");

      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      if (!idToken) throw new Error("Not signed in");

      const data = await http(`${API_BASE}/me/subscription`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      setStatusOut(data);
      setNote("✅ Loaded (by userId)");
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setStatusOut(null);
    } finally {
      setBusy(false);
    }
  };

  // Test override path: GET /me/subscription?customerId=...
  // Requires your backend to allow overrides (ALLOW_QUERY_OVERRIDE=true)
  const checkByCustomerId = async () => {
    try {
      if (!/^cus_/.test(customerId || "")) {
        setNote("Enter a valid customer id (cus_…)");
        return;
      }
      setBusy(true);
      setNote("Fetching subscription by customerId…");

      // Override route is not protected in your code sample, but if you do protect it:
      let headers = {};
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (idToken) headers.Authorization = `Bearer ${idToken}`;
      } catch { /* okay */ }

      const data = await http(
        `${API_BASE}/me/subscription?customerId=${encodeURIComponent(customerId)}`,
        { headers }
      );

      setStatusOut(data);
      setNote("✅ Loaded (by customerId override)");
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setStatusOut(null);
    } finally {
      setBusy(false);
    }
  };

  const clearLocal = () => {
    localStorage.removeItem("stripeCustomerId");
    setCustomerId("");
    setStatusOut(null);
    setNote("Cleared local customerId");
  };

  return (
    <main className="mx-auto max-w-2xl p-6">
      <h1 className="text-2xl font-semibold mb-4">Subscription Test</h1>

      {/* Who am I? */}
      <section className="mb-6 p-4 border rounded">
        <h2 className="font-medium mb-2">Signed-in user (claims)</h2>
        {meOut ? (
          <pre className="text-sm overflow-auto bg-gray-50 p-3 rounded">
            {JSON.stringify(meOut, null, 2)}
          </pre>
        ) : (
          <p className="text-sm text-gray-600">Not signed in.</p>
        )}
      </section>

      {/* Start subscription */}
      <section className="mb-6 p-4 border rounded">
        <h2 className="font-medium mb-3">Start subscription</h2>
        <button
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          onClick={startSubscription}
          disabled={busy}
        >
          Start checkout
        </button>
      </section>

      {/* Read status via userId (GET /me/subscription) */}
      <section className="mb-6 p-4 border rounded">
        <h2 className="font-medium mb-3">Check status (by userId)</h2>
        <button
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          onClick={checkMySubscription}
          disabled={busy}
        >
          Get my subscription status
        </button>
      </section>

      {/* Read status via override (GET /me/subscription?customerId=...) */}
      <section className="mb-6 p-4 border rounded">
        <h2 className="font-medium mb-3">Check status (by customerId override)</h2>

        <label className="block text-sm mb-1">Customer ID (cus_…)</label>
        <input
          className="w-full border rounded px-3 py-2 mb-3"
          value={customerId}
          placeholder="cus_..."
          onChange={(e) => {
            setCustomerId(e.target.value);
            if (e.target.value) localStorage.setItem("stripeCustomerId", e.target.value);
          }}
        />

        <div className="flex gap-2">
          <button
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
            onClick={checkByCustomerId}
            disabled={busy}
          >
            Get by customerId
          </button>
          <button
            className="px-3 py-2 rounded border"
            onClick={clearLocal}
            disabled={busy}
          >
            Clear stored customerId
          </button>
        </div>
      </section>

      {/* Output */}
      {note && <p className="text-sm text-gray-700 mb-3">{note}</p>}

      {statusOut && (
        <section className="mb-6 p-4 border rounded">
          <h2 className="font-medium mb-2">Subscription Response</h2>
          <div className="mb-2">
            <StatusBadge status={statusOut.status} />
          </div>
          <pre className="text-sm overflow-auto bg-gray-50 p-3 rounded">
            {JSON.stringify(statusOut, null, 2)}
          </pre>
        </section>
      )}
    </main>
  );
}

// Tiny status badge for quick visual feedback
function StatusBadge({ status }) {
  const s = (status || "none").toLowerCase();
  const color =
    s === "active"
      ? "#16a34a"
      : s === "trialing"
      ? "#0ea5e9"
      : s === "past_due"
      ? "#ca8a04"
      : s === "incomplete"
      ? "#ef4444"
      : "#6b7280";
  const style = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "9999px",
    fontSize: 12,
    background: "#f3f4f6",
    border: `1px solid ${color}`,
    color,
  };
  return <span style={style}>{s}</span>;
}
